import React, { useEffect, useMemo } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom'
import { LanguageProvider } from './context/LanguageContext'
import { FactoryProvider } from './context/FactoryContext'
import { TerminalProvider } from './context/TerminalContext'
import Layout from './components/Layout.jsx'
import { useAuth } from './hooks/useAuth'
import { usePermissions } from './hooks/usePermissions'
import { useFactory } from './context/FactoryContext'
import { logout } from './utils/auth'
import {
  canAccessRoute,
  getDefaultHomePath,
  ACCESS_DENIED_PATH,
  logRouteAccessSession,
  logRouteAccessDenied,
} from './utils/routeAccess'

// Importar rutas modulares
import { allRoutes, validateRoutes, publicRoutes } from './routes/index.jsx'

import './App.css'

// Nota: La interceptación de /auth/* se maneja en:
// 1. index.html (script inline) - se ejecuta antes de que React se cargue
// 2. Verificación en App() antes de montar Router - como medida de seguridad

function DefaultHomeRedirect() {
  const { permissions, isLoading } = usePermissions()
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '40vh',
      }}
      >
        <p style={{ color: '#94a3b8' }}>Cargando…</p>
      </div>
    )
  }
  const home = permissions ? getDefaultHomePath(permissions) : '/dashboard'
  return <Navigate to={home} replace />
}

// Componente wrapper para Layout que obtiene los datos necesarios
function LayoutWrapper() {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuth()
  const { permissions, isLoading } = usePermissions()

  // Obtener fábricas disponibles desde permisos
  const factories = useMemo(() => {
    if (!permissions?.organization?.factory_names) {
      return []
    }
    return permissions.organization.factory_names
  }, [permissions])

  // Fábrica seleccionada desde contexto (persistida en ?factory= de la URL)
  const { selectedFactory, setSelectedFactory } = useFactory()

  // Valor por defecto: solo se aplica cuando no hay selección activa (ni en URL ni en contexto)
  useEffect(() => {
    if (factories.length > 0 && selectedFactory === null) {
      const hasCX = factories.includes('CX')
      // Si el usuario tiene acceso global (CX), dejamos null (vista global)
      // Si no, seleccionamos la primera fábrica disponible y actualizamos la URL
      setSelectedFactory(hasCX ? null : factories[0])
    }
  }, [factories, selectedFactory])

  // Redirigir si la URL tiene prefijo de fábrica como path (ej: /CXC/dashboard -> /dashboard?factory=CXC)
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    const factoryCodes = ['CXB', 'CXC', 'CXD', 'CXE', 'CXF', 'CXM', 'EXT', 'FPC', 'FPL', 'FSP', 'MNT', 'RTP', 'ITC', 'CX']
    
    if (pathParts.length > 0 && factoryCodes.includes(pathParts[0].toUpperCase())) {
      const factoryCode = pathParts[0].toUpperCase()
      const rest = pathParts.slice(1).join('/')
      const newPath = '/' + (rest || 'dashboard')
      // No reescribir rutas PDA (viven fuera del Layout); por si acaso llegan aquí
      if (newPath === '/pda/sala-mezcla' || newPath.startsWith('/pda/')) return

      const params = new URLSearchParams(location.search)
      if (!params.get('factory')) {
        params.set('factory', factoryCode)
      }
      const qs = params.toString()
      navigate({ pathname: newPath, search: qs ? `?${qs}` : '' }, { replace: true })
    }
  }, [location.pathname, location.search, navigate])

  // Nombre de usuario
  const userData = auth?.apiUserData?.user || auth?.serverAuth?.user || auth?.user || {};
  const userName = userData.name || 'Usuario';

  // Handler para logout
  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  useEffect(() => {
    if (!isLoading && permissions) {
      logRouteAccessSession(permissions)
    }
  }, [permissions, isLoading])

  const pathDenied =
    Boolean(permissions) &&
    !isLoading &&
    location.pathname !== ACCESS_DENIED_PATH &&
    !canAccessRoute(location.pathname, permissions)

  if (pathDenied) {
    const redirectTo = getDefaultHomePath(permissions)
    // Evitar bucle Navigate → misma ruta denegada
    if (redirectTo === location.pathname) {
      return (
        <Layout
          onLogout={handleLogout}
          userName={userName}
          factories={factories}
          navigate={navigate}
        >
          <Outlet />
        </Layout>
      )
    }
    logRouteAccessDenied(location.pathname, permissions, redirectTo)
    const target =
      redirectTo === ACCESS_DENIED_PATH
        ? { pathname: ACCESS_DENIED_PATH, search: location.search }
        : { pathname: redirectTo, search: location.search }

    return (
      <Layout
        onLogout={handleLogout}
        userName={userName}
        factories={factories}
        navigate={navigate}
      >
        <Navigate
          to={target}
          state={{ from: location.pathname }}
          replace
        />
      </Layout>
    )
  }

  return (
    <Layout
      onLogout={handleLogout}
      userName={userName}
      factories={factories}
      navigate={navigate}
    >
      <Outlet />
    </Layout>
  )
}

function App() {
  // Validar rutas al cargar la aplicación (solo en desarrollo)
  useEffect(() => {
    if (import.meta.env.DEV) {
      validateRoutes()
    }
  }, [])

  // Nota: La interceptación de /auth/* se maneja en index.html (script inline)
  // Este código es solo una medida de seguridad por si acaso
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/')) {
     const apiBase = import.meta.env.VITE_API_URL.replace(/\/+$/, '')
    const fullURL = `${apiBase}${window.location.pathname}${window.location.search}${window.location.hash}`
    window.location.replace(fullURL)
    
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Redirigiendo...</p>
      </div>
    )
  }

  return (
    <LanguageProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
      <FactoryProvider>
      <TerminalProvider>
      <div className="App">
        <Routes>
          {/* Páginas públicas sin Layout */}
          {publicRoutes}
          
          {/* Rutas con Layout */}
          <Route element={<LayoutWrapper />}>
            <Route path="/" element={<DefaultHomeRedirect />} />
            
            {/* Todas las rutas protegidas - Importadas desde módulos */}
            {allRoutes.filter(route => {
              // Filtrar solo las rutas que NO son públicas (las públicas ya están arriba)
              const isPublicRoute = publicRoutes.some(publicRoute => 
                publicRoute.props.path === route.props.path
              )
              return !isPublicRoute
            })}
          </Route>
        </Routes>
      </div>
      </TerminalProvider>
      </FactoryProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App;
